import { createSlice } from "@reduxjs/toolkit";
import Swal from "sweetalert2";
import { Call, CallWithFormData, objectToFormData } from "@helpers/fetch";
import { errorHandler } from "@helpers/errorHandler";
import {
    startUILoading,
    stopUILoading,
} from "../../actions/administrador/ui/ui";
import { deleteConfirmModal } from "@helpers/DeleteValidate";

const initialState = {
    list: [],
    pagination: {
        data: [],
        per_page: 10,
        total: 0,
        current_page: 1,
        last_page: 1,
    },
    documents: [],
    active: null,
};

export const CustomersSlice = createSlice({
    name: "customers",
    initialState,
    reducers: {
        setList: (state, action) => {
            state.list = action.payload;
        },
        setPagination: (state, action) => {
            state.pagination = action.payload;
        },
        setActive: (state, action) => {
            state.active = action.payload;
        },
        clearActive: (state) => {
            state.active = null;
        },

        setCustomerDocuments: (state, action) => {
            state.documents = action.payload;
        },
    },
});

export const {
    setList,
    setPagination,
    setActive,
    clearActive,
    setCustomerDocuments,
} = CustomersSlice.actions;

export default CustomersSlice.reducer;

export const startShowCustomers = ({
    page = 1,
    query = "",
    number_rows = 10,
    person_type = "",
    enterprise_id = "",
    asList = false,
} = {}) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const params = new URLSearchParams();

        params.set("page", page);
        params.set("per_page", number_rows);

        if (query) params.set("q", query);
        if (person_type) params.set("person_type", person_type);
        if (enterprise_id) params.set("enterprise_id", enterprise_id);
        if (asList) params.set("list", asList);

        const resp = await Call("users/customers", "GET", params.toString());

        if (resp?.success?.customers) {
            if (asList) {
                dispatch(setList(resp.success.customers));
            } else {
                dispatch(setPagination(resp.success.customers));
            }
        } else {
            errorHandler(resp);
        }

        dispatch(stopUILoading());
    };
};

export const startFindCustomer = (customer_id, enterprise_id) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const params = new URLSearchParams();
        params.set("customer_id", customer_id);
        if (enterprise_id) params.set("enterprise_id", enterprise_id);

        const resp = await Call("users/customers", "GET", params.toString());

        if (resp?.success?.customer) {
            dispatch(setActive(resp.success.customer));
        } else {
            errorHandler(resp);
        }

        dispatch(stopUILoading());

        return resp;
    };
};

export const startCreateCustomer = (data, setErrors = () => {}) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call("users/customers/create", "POST", data);

        if (resp?.success) {
            Swal.fire("Guardado", "Cliente creado correctamente", "success");
            dispatch(setActive(resp.success));
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());

        return resp;
    };
};

export const startUpdateCustomer = (
    customer_id,
    data,
    setErrors = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `users/customers/edit/${customer_id}`,
            "POST",
            data
        );

        if (resp?.success) {
            Swal.fire(
                "Guardado",
                "Cliente actualizado correctamente",
                "success"
            );
            dispatch(setActive(resp.success));
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());

        return resp;
    };
};

export const startUpdateAvatar = (customer_id, image) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        let formData = new FormData();
        formData.append("image", image);

        const resp = await CallWithFormData(
            `users/customers/edit/${customer_id}`,
            "POST",
            formData
        );

        dispatch(setActive(resp.success));
        dispatch(stopUILoading());
    };
};

export const startDeleteCustomer = (id, currentValues) => {
    return async (dispatch) => {
        await deleteConfirmModal(async () => {
            dispatch(startUILoading());
            const params = new URLSearchParams();
            if (currentValues?.enterprise_id) {
                params.set("enterprise_id", currentValues.enterprise_id);
            }
            const resp = await Call(
                `users/customers/${id}?${params.toString()}`,
                "DELETE"
            );
            if (resp.success) {
                dispatch(startShowCustomers(currentValues));
                Swal.fire({
                    title: "Éxito",
                    text: "Eliminado correctamente.",
                    icon: "success",
                });
            } else {
                errorHandler(resp);
            }
            dispatch(stopUILoading());
        });
    };
};

export const startGetCustomerDocuments = (customer_id) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `users/customers/documents/${customer_id}`,
            "GET"
        );

        if (resp.success) {
            dispatch(setCustomerDocuments(resp.success));
        }

        dispatch(stopUILoading());
    };
};

export const startUploadCustomerDocument = (
    { customer_document_id, uploadedFile, customer_id },
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const formData = objectToFormData({
            customer_document_id,
            uploadedFile,
            customer_id,
        });

        const resp = await CallWithFormData(
            "users/customers/documents/upload",
            "POST",
            formData
        );

        if (resp.success) {
            callback();
            dispatch(startGetCustomerDocuments(customer_id));
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startReviewCustomerDocument = (
    customerDocumentId,
    data = {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `users/customers/documents/review/${customerDocumentId}`,
            "PATCH",
            data
        );

        if (resp.success) {
            callback();
        } else {
            errorHandler(resp);
        }

        dispatch(stopUILoading());
    };
};
